import { Link } from "react-router-dom";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import type { HubFooterProps, HubHeaderProps } from "@/types/help-hub/presentation";
import { hubBrandTheme } from "../brandInk";

/** First letters of the business name, for when they have no logo uploaded. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("") || "?";

/**
 * The full Help Genie lockup from the home page — mark, wordmark and tagline —
 * not just the G. Dark ink because hub chrome is always white.
 */
const HelpGenieLockup = ({ iconSize = 30 }: { iconSize?: number }) => (
  <span className="inline-flex items-center gap-2">
    <HelpGenieLogo size={iconSize} />
    <span className="flex flex-col text-left leading-tight">
      <span className="text-[15px] font-bold tracking-tight text-genie-text-primary">
        Help Genie
      </span>
      <span className="text-[10.5px] text-genie-text-muted">Now We're Talking</span>
    </span>
  </span>
);

/**
 * A solid white bar on every page, crowned with a ribbon of the customer's
 * colours. Their crest — logo included — leads on the left; the full Help Genie
 * lockup sits on the right, on white — never on top of an arbitrary brand
 * gradient, where it used to disappear.
 */
export const HubHeader = ({ name, branding, homeHref, wide = false }: HubHeaderProps) => {
  const theme = hubBrandTheme(branding);
  const crest = (
    <div className="flex min-w-0 items-center gap-3">
      {branding.logoUrl ? (
        <img
          src={branding.logoUrl}
          alt=""
          className="h-11 w-11 shrink-0 rounded-xl bg-white object-contain ring-1 ring-black/[0.06]"
        />
      ) : (
        <div
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-sm font-semibold text-white"
          style={{ background: theme.gradient }}
        >
          {initialsOf(branding.brandName || name)}
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate text-[16px] font-semibold leading-tight text-genie-text-primary">
          {branding.brandName || name}
        </div>
        <div className="truncate text-[12.5px] text-genie-text-muted">
          {branding.tagline || "Help Centre"}
        </div>
      </div>
    </div>
  );

  return (
    <header className="relative border-b border-genie-border bg-white">
      {/* The customer's colours framing the page from the very first pixel. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: theme.gradient }}
      />
      <div className={`mx-auto flex flex-wrap items-center justify-between gap-4 py-4 ${wide ? "hub-page-width" : "max-w-5xl px-5"}`}>
        {homeHref ? (
          <Link
            to={homeHref}
            className="min-w-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ outlineColor: theme.primary }}
          >
            {crest}
          </Link>
        ) : (
          crest
        )}
        <a
          href="/"
          aria-label="Powered by Help Genie"
          className="hidden min-h-11 items-center gap-2.5 sm:inline-flex"
        >
          <span className="text-[11.5px] text-genie-text-muted">Powered by</span>
          <HelpGenieLockup />
        </a>
      </div>
    </header>
  );
};

/**
 * Branded empty state. Used when the slug resolves to nothing public — a
 * private Genie, a typo, or a hub that was never switched on. Deliberately not
 * an error page: a visitor who mistyped a URL has done nothing wrong.
 */
export const HubNotFound = () => (
  <div className="flex min-h-[100dvh] items-center justify-center bg-genie-background px-6">
    <div className="max-w-md text-center">
      <h1 className="text-2xl font-bold text-genie-text-primary">
        This help centre isn't available
      </h1>
      <p className="mt-2 text-genie-text-sub">
        The link may be out of date, or this business hasn't published their help
        centre yet.
      </p>
    </div>
  </div>
);

export const HubFooter = ({
  name,
  branding,
  wide = false,
}: HubFooterProps) => (
  <footer className="relative border-t border-genie-border bg-white">
    {/* Same brand ribbon as the header, closing the page in their colours. */}
    <div
      aria-hidden
      className="absolute inset-x-0 top-0 h-1"
      style={{ background: hubBrandTheme(branding).gradient }}
    />
    <div className={`mx-auto flex flex-wrap items-center justify-between gap-4 py-6 ${wide ? "hub-page-width" : "max-w-5xl px-5"}`}>
      <div className="text-[12.5px] text-genie-text-muted">{name} · Help Centre</div>
      <a
        href="/"
        aria-label="Powered by Help Genie"
        className="inline-flex min-h-11 items-center gap-2.5"
      >
        <span className="text-[11.5px] text-genie-text-muted">Powered by</span>
        <HelpGenieLockup iconSize={26} />
      </a>
    </div>
  </footer>
);
